/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import CardLayout from "../Cards/CardLayout";
import { useFetchData } from "../../customHooks/useGetDashboardData";
import Loader from "../feedBacks/loader";
import { createToolboxConfig,  formatCustomDate } from "../../utils/utils";
import CardError from "../feedBacks/CardError";
import { REFETCH_CONFIGURATION } from "../../utils/constants";
import { EChartsOption } from "echarts";
import "../../styles/custom-styles.css"
import { useUtils } from "../../customHooks/useUtils";
import { useAppSelector } from "../../store/hooks";
interface LoadRateResponse {
  load: number;
  time: string;
}

const GaugeChart = ({ site_id}: { site_id: string }) => {
  const { FEEDBACK_MESSAGES, t } = useUtils();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const language = useAppSelector((state) => state.language.language);
  
  const { data, isLoading, error } = useFetchData<LoadRateResponse>(
    ["/operations", "fetchTransformerLoadRate", site_id],
    `/operations/site/transformer-load/${site_id}`,
    {},
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
    }
  );

  useEffect(() => {
    // Initialize chart if DOM element is ready
    if (chartRef.current && !chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // Update chart if data is available and chart is initialized
    if (chartInstance.current && data) {
      const loadValue = data?.load || 0;

      const option: EChartsOption = {
        ...createToolboxConfig({
          chartName: t("chart.transformer_load_rate"),
          time: (data?.time && formatCustomDate(data?.time)) || "",
          top: '5px',
          right: '5px',
        }),
        grid: {
          top: 10,
          bottom: 10,
          left: 10,
          right: 10,
        },
        series: [
          {
            type: "gauge",
            min: 0,
            max: 150,
            radius: "105%", // Increase the size of the gauge
            center: ["50%", "65%"], // Adjust vertical position
            startAngle: 200,
            endAngle: -20,
            axisLine: {
              lineStyle: {
                width: 40, // Increased line width
                color: [
                  [0.2, "#3b82f6"], // 0 - 30%
                  [0.33, "#16a34a"], // 31 - 50%
                  [0.53, "#86efac"], // 51 - 80%
                  [0.67, "#eab308"], // 81 - 100%
                  [0.8, "#f97316"], // 101 - 120%
                  [1.0, "#dc2626"], // > 120%
                ],
              },
            },
            pointer: {
              itemStyle: {
                color: "auto",
              },
              width: 8, // Increased pointer width
              length: "60%", // Adjusted pointer length
            },
            axisTick: {
              distance: -45,
              length: 12,
              lineStyle: {
                color: "#fff",
                width: 2,
              },
              splitNumber: 5,
            },
            splitLine: {
              distance: -45,
              length: 40,
              lineStyle: {
                color: "#fff",
                width: 4,
              },
            },
            axisLabel: {
              color: "inherit",
              distance: 50,
              fontSize: 16,
              formatter: "{value}",
            },
            detail: {
              valueAnimation: true,
              formatter: "{value}%",
              color: "#333",
              fontSize: 32,
              fontWeight: "bold",
              offsetCenter: [0, "40%"],
            },
            title: {
              fontSize: 14,
              offsetCenter: [0, "80%"],
            },
            data: [
              {
                value: loadValue,
                name: "Load Rate",
              },
            ],
          },
        ],
      };

      chartInstance.current.setOption(option);
    }

    // Handle resize
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [data, t]);

  // Render loading state
  if (isLoading) {
    return (
      <CardLayout
        title={t("chart.transformer_load_rate")}
        className="lg:min-w-[400px] flex-1"
      >
        <div className="flex justify-center items-center h-full">
          <Loader />
        </div>
      </CardLayout>
    );
  }

  // Render error state
  if (error) {
    return (
      <CardLayout
        title={t("chart.transformer_load_rate")}
        className="lg:min-w-[400px] flex-1"
      >
        <CardError message={FEEDBACK_MESSAGES.ERROR} />
      </CardLayout>
    );
  }

  // Render chart
  return (
    <CardLayout
      title={t("chart.transformer_load_rate")}
      className={`w-full min-w-full !flex-1 !flex !items-center !justify-center ${
        window.innerWidth >= 1170 ? "!min-w-[250px] !w-full" : ""
      } load-rate-custom-style`}
    >
      <div ref={chartRef} className="w-full h-full" />
      {
        <p className="text-xs mt-1 text-center text-primary-yellowMain">
          {data?.time && formatCustomDate(data?.time as string, language)}
        </p>
      }
    </CardLayout>
  );
};

export default GaugeChart;
